package one.healthium.uscreen.ui.layout

import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.layout.Measurable
import androidx.compose.ui.layout.Placeable
import androidx.compose.ui.layout.layout
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.dp
import kotlin.math.max
import kotlin.math.roundToInt

private const val SCROLL_EXTRA_BOTTOM_DP = 80

/**
 * Variant D: Single-pass custom Layout.
 *
 * Measures all children in one pass. Flex spacers are identified by a special
 * [FlexSpacerParentData] tag. If total non-flex height > available → scrollable mode,
 * flex spacers get fallbackFlexDp. If ≤ available → remaining space is distributed
 * among flex spacers proportionally to their flex values (like Column weight).
 *
 * Single measure pass. Maximum performance. No recomposition.
 */
@Composable
fun CustomMeasureBlockLayout(
    modifier: Modifier = Modifier,
    fallbackFlexDp: Int = 24,
    bottomPadding: Int = 0,
    content: @Composable () -> Unit,
) {
    val scrollState = rememberScrollState()
    val density = LocalDensity.current
    val fallbackFlexPx = with(density) { fallbackFlexDp.dp.toPx().roundToInt() }
    val bottomPaddingPx = with(density) { bottomPadding.dp.toPx().roundToInt() }
    val scrollExtraBottomPx = with(density) { SCROLL_EXTRA_BOTTOM_DP.dp.toPx().roundToInt() }
    // Capture real available height before verticalScroll replaces it with Infinity
    val availableHeightHolder = remember { intArrayOf(0) }

    Layout(
        modifier = modifier
            .layout { measurable, constraints ->
                availableHeightHolder[0] = constraints.maxHeight
                val placeable = measurable.measure(constraints)
                layout(placeable.width, placeable.height) {
                    placeable.place(0, 0)
                }
            }
            .verticalScroll(scrollState)
            .testTag("uscreen_custom_layout"),
        content = content,
    ) { measurables, constraints ->
        val maxWidth = constraints.maxWidth
        // constraints.maxHeight is Infinity due to verticalScroll — use captured real height
        val maxHeight = availableHeightHolder[0]

        // First: measure all non-flex children to know fixed content height
        data class ChildInfo(
            val measurable: Measurable,
            val flexValue: Float?,
            var placeable: Placeable? = null,
        )

        val children = measurables.map { measurable ->
            val flex = measurable.parentData as? FlexSpacerData
            ChildInfo(measurable, flex?.flex)
        }

        var fixedHeight = bottomPaddingPx
        val flexChildren = mutableListOf<ChildInfo>()

        // Measure non-flex children
        children.forEach { child ->
            if (child.flexValue != null) {
                flexChildren.add(child)
            } else {
                val placeable = child.measurable.measure(Constraints(maxWidth = maxWidth))
                child.placeable = placeable
                fixedHeight += placeable.height
            }
        }

        val totalFlexWeight = flexChildren.sumOf { it.flexValue!!.toDouble() }.toFloat()
        val remainingSpace = maxHeight - fixedHeight
        val needsScroll = remainingSpace < 0

        // Measure flex children
        flexChildren.forEach { child ->
            val height = if (needsScroll || totalFlexWeight <= 0f) {
                fallbackFlexPx
            } else {
                ((child.flexValue!! / totalFlexWeight) * remainingSpace).roundToInt()
                    .coerceAtLeast(0)
            }
            child.placeable = child.measurable.measure(
                Constraints.fixed(maxWidth, height),
            )
        }

        val scrollExtraPx = if (needsScroll) scrollExtraBottomPx else 0
        val totalHeight = children.sumOf { it.placeable!!.height } + bottomPaddingPx + scrollExtraPx
        val layoutHeight = if (needsScroll) totalHeight else max(totalHeight, maxHeight)

        layout(maxWidth, layoutHeight) {
            var y = 0
            children.forEach { child ->
                child.placeable!!.place(0, y)
                y += child.placeable!!.height
            }
        }
    }
}

/**
 * Parent data marker for flex spacers in CustomMeasureBlockLayout.
 */
data class FlexSpacerData(val flex: Float)

/**
 * Modifier to tag a composable as a flex spacer for CustomMeasureBlockLayout.
 */
fun Modifier.flexSpacer(flex: Float): Modifier = this.then(
    object : androidx.compose.ui.layout.ParentDataModifier {
        override fun androidx.compose.ui.unit.Density.modifyParentData(parentData: Any?): Any =
            FlexSpacerData(flex)
    },
)
